import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { apiErrorCode, apiErrorMessage } from '@/api/client'
import { bankAccounts as api } from '@/api/endpoints'
import { Alert, Input, Spinner } from '@/components/ui'
import { isValidIban } from '@/lib/iban'
import { cn } from '@/lib/utils'
import type { BankAccount } from '@/types/api'

/**
 * Účty (IBAN) strediska (`sql/042`) - viac účtov, jeden predvolený. Na
 * predvolený sa platí, keď rezervácia iný účet neurčí. Používa ho Nastavenia
 * → Systém (hlavné stredisko, v každom balíčku) aj detail strediska.
 */
export default function BankAccounts({ centerId, onChanged }: { centerId: number; onChanged?: () => void }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['bank-accounts', centerId],
    queryFn: () => api.list(centerId),
  })
  const [drafts, setDrafts] = useState<Record<number, { name: string; iban: string }>>({})
  const [fresh, setFresh] = useState({ name: '', iban: '' })
  const [message, setMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null)

  useEffect(() => {
    if (data) setDrafts(Object.fromEntries(data.map((a) => [a.id, { name: a.name, iban: a.iban }])))
  }, [data])

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['bank-accounts'] })
    // Výber účtu v rezervácii ide z `bootstrap`.
    void qc.invalidateQueries({ queryKey: ['bootstrap'] })
    onChanged?.()
  }
  const onError = (err: unknown) => {
    const body = (err as { response?: { data?: Record<string, unknown> } }).response?.data
    const ibanError = body && Array.isArray(body.iban) ? String(body.iban[0]) : null
    setMessage({
      kind: 'error',
      text:
        apiErrorCode(err) === 'account.inUse'
          ? t('accounts.inUse')
          : ibanError
            ? t('accounts.invalidIban')
            : apiErrorMessage(err),
    })
  }
  const saved = () => {
    setMessage({ kind: 'success', text: t('global.saved') })
    refresh()
  }
  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<BankAccount> }) => api.update(id, body),
    onSuccess: saved,
    onError,
  })
  const create = useMutation({
    mutationFn: () => api.create({ center_id: centerId, name: fresh.name.trim(), iban: fresh.iban.trim() }),
    onSuccess: () => {
      setFresh({ name: '', iban: '' })
      saved()
    },
    onError,
  })
  const remove = useMutation({ mutationFn: (id: number) => api.remove(id), onSuccess: saved, onError })

  if (isLoading || !data) {
    return (
      <div className="flex h-12 items-center text-slate-400">
        <Spinner className="h-4 w-4" />
      </div>
    )
  }

  const freshValid = isValidIban(fresh.iban)

  return (
    <div className="space-y-2">
      {message ? <Alert variant={message.kind}>{message.text}</Alert> : null}
      {data.length ? (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase text-slate-500">
              <th className="pb-1 pr-2 font-medium">{t('accounts.name')}</th>
              <th className="pb-1 pr-2 font-medium">IBAN</th>
              <th className="pb-1 pr-2 font-medium">{t('accounts.default')}</th>
              <th className="pb-1 pr-2 font-medium">{t('accounts.active')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.map((account) => {
              const draft = drafts[account.id] ?? { name: account.name, iban: account.iban }
              const ibanBad = draft.iban.trim() !== '' && !isValidIban(draft.iban)
              return (
                <tr key={account.id} className={cn('align-top', !account.active && 'opacity-60')}>
                  <td className="py-1 pr-2">
                    <Input
                      value={draft.name}
                      maxLength={100}
                      placeholder={t('accounts.namePlaceholder')}
                      onChange={(e) => setDrafts({ ...drafts, [account.id]: { ...draft, name: e.target.value } })}
                      onBlur={() => {
                        if (draft.name.trim() !== account.name)
                          update.mutate({ id: account.id, body: { name: draft.name.trim() } })
                      }}
                    />
                  </td>
                  <td className="py-1 pr-2">
                    <Input
                      className={cn('font-mono', ibanBad && 'border-red-400')}
                      value={draft.iban}
                      onChange={(e) => setDrafts({ ...drafts, [account.id]: { ...draft, iban: e.target.value } })}
                      onBlur={() => {
                        const next = draft.iban.trim()
                        if (next === account.iban) return
                        if (!isValidIban(next)) {
                          setMessage({ kind: 'error', text: t('accounts.invalidIban') })
                          return
                        }
                        update.mutate({ id: account.id, body: { iban: next } })
                      }}
                    />
                  </td>
                  <td className="py-2 pr-2 text-center">
                    <input
                      type="radio"
                      name={`default-account-${centerId}`}
                      checked={account.is_default}
                      disabled={!account.active || update.isPending}
                      onChange={() => update.mutate({ id: account.id, body: { is_default: true } })}
                      aria-label={t('accounts.default')}
                    />
                  </td>
                  <td className="py-2 pr-2 text-center">
                    <input
                      type="checkbox"
                      checked={account.active}
                      disabled={update.isPending}
                      onChange={(e) => update.mutate({ id: account.id, body: { active: e.target.checked } })}
                      aria-label={t('accounts.active')}
                    />
                  </td>
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      className="text-slate-400 hover:text-red-600"
                      title={t('global.delete')}
                      onClick={() => {
                        if (window.confirm(t('accounts.deleteConfirm', { iban: account.iban }))) remove.mutate(account.id)
                      }}
                    >
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      ) : (
        <p className="text-xs text-amber-700">{t('accounts.none')}</p>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <Input
          className="w-48"
          maxLength={100}
          placeholder={t('accounts.namePlaceholder')}
          value={fresh.name}
          onChange={(e) => setFresh({ ...fresh, name: e.target.value })}
        />
        <Input
          className="w-72 font-mono"
          placeholder="SK31 1200 0000 1987 4263 7541"
          value={fresh.iban}
          onChange={(e) => setFresh({ ...fresh, iban: e.target.value })}
        />
        <button
          type="button"
          className="btn-secondary"
          disabled={!freshValid || create.isPending}
          onClick={() => {
            setMessage(null)
            create.mutate()
          }}
        >
          {create.isPending ? <Spinner /> : <Plus size={14} />}
          {t('accounts.add')}
        </button>
      </div>
      <p className="text-xs text-slate-500">{t('accounts.hint')}</p>
    </div>
  )
}
